
import React from 'react';
import { NetworkDevice, NodeType, SensorModel } from '../types';

interface Props {
  device: NetworkDevice;
  isSelected: boolean;
  onClick: (id: string) => void;
}

const getSensorIcon = (model?: SensorModel) => {
  switch (model) {
    case 'DHT11': return (
      <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    );
    case 'PIR': return (
      <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    );
    case 'HC-SR04': return (
      <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 11h8m-9 4h10" />
      </svg>
    );
    case 'MQ-135': return (
      <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
    default: return (
      <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  }
};

const icons: Record<NodeType, (device: NetworkDevice) => React.ReactNode> = {
  DASHBOARD: () => (
    <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  SENSOR: (d) => getSensorIcon(d.sensorModel),
  CONTROLLER: () => (
    <div className="relative">
      <div className="absolute -inset-1 bg-indigo-400/20 rounded-full animate-ping"></div>
      <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    </div>
  ),
  EXTENDER: () => (
    <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0" />
    </svg>
  ),
  CLOUD: () => (
    <div className="relative flex items-center justify-center">
      <div className="absolute w-12 h-12 bg-indigo-400 rounded-full blur-md opacity-20 animate-pulse"></div>
      <svg className="w-12 h-12 relative z-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    </div>
  )
};

const DeviceNode: React.FC<Props> = ({ device, isSelected, onClick }) => {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick(device.id);
      }}
      className={`w-20 h-20 flex flex-col items-center justify-center rounded-xl transition-all transform z-10 select-none cursor-pointer relative ${
        isSelected ? 'bg-indigo-50 ring-4 ring-indigo-400 scale-105 shadow-xl' : 'bg-white shadow-md hover:shadow-lg'
      } ${device.type === 'CLOUD' ? 'border-2 border-indigo-200 bg-indigo-50/50 shadow-indigo-100 shadow-lg' : ''}`}
    >
      <div className="mb-0.5">
        {icons[device.type](device)}
      </div>
      
      <span className="text-[7px] font-black uppercase tracking-tighter text-slate-400">
        {device.sensorModel || (device.type === 'CONTROLLER' ? 'ESP32' : device.type)}
      </span>
      
      <span className="text-[9px] font-bold text-slate-800 truncate w-full px-1 text-center leading-tight">
        {device.name}
      </span>

      {isSelected && (
        <div className="absolute -bottom-6 left-0 right-0 text-center">
          <span className="bg-indigo-600 text-white text-[8px] px-2 py-0.5 rounded-full font-black uppercase shadow-lg tracking-widest animate-bounce">
            SELECT
          </span>
        </div>
      )}
    </div>
  );
};

export default DeviceNode;
